'use strict';

define('features/slp/vehicle.picker',[
  'jquery',
  'lib/page-cache',
  'txt!./vehicle.picker.hbs',
  'features/recentVehicles',
  'lib/services/batws',
  'lib/analytics/context.add',
  'lib/analytics/blocks/index',

  // side effect includes
  'Handlebars',
  'lib/helpers/ifCond',
  'lib/loader/series',
  'lib/transforms/series.mapByCode',
  'dropkick'
], function (
  $,
  PageCache,
  txtTemplate,
  recentVehicles,
  batws,
  anAddContext,
  blocks
) {
  var __tmplVehiclePicker = Handlebars.compile(txtTemplate);

  function __parseSeries(seriesArr, seriesMap) {
    var vehSelect = [];
    seriesArr.forEach(function (series) {
      var displayName = seriesMap[series.series_code].series_display_name;
      if (BATSiteConfig.disabledVehicles.indexOf(series.series_code) === -1) {
        vehSelect.push({
          id: series.year + '|' + series.series_code,
          title: series.year + ' ' + displayName
        });
      }
    });
    return vehSelect;
  }

  // returns an xformed seriesArr
  function __markSelectedVehicle(_series, selectedVehicle) {
    var seriesWithSelected = _series;
    if (selectedVehicle) {
      seriesWithSelected = _series.map(function (item) {
        var currItem = item;
        if (item.id === selectedVehicle) {
          currItem.selected = true;
        } else {
          delete currItem.selected;
        }
        return currItem;
      });
    }
    return seriesWithSelected;
  }

  function __vehicleImage(vehicleId) {
    if (vehicleId) {
      var veh = vehicleId.split('|');
      return '/assets/img/vehicles/' + veh[1] + '/' + veh[0] + '/default-offer-card.png';
    }
    return '/assets/img/slp-vehicle-placeholder.png';
  }

  function __normalize(txt) {
    return txt.replace(/\s+/g, '').replace('-', '').toLowerCase();
  }

  // If we have a recently saved vehicle, and it has a trim
  // we need to mark that one as selected
  // 'sr5' === 'sr5'
  function __isSelectedModel(item, idx) {
    var mostRecent = recentVehicles.getMostRecentVehicle();
    if (mostRecent && mostRecent.trim) {
      return __normalize(item) === __normalize(mostRecent.trim);
    }
    return idx === 0;
  }

  function __getSelectedModel(models) {
    var selectedModel = models.find(function (model) {
      return model.selected;
    });
    return selectedModel;
  }

  function __modelsForCtx(modelsArr) {
    var modelsForCtx = modelsArr.map(function (item, idx) {
      var tmpObj = {
        id: __normalize(item),
        title: item,
        selected: __isSelectedModel(item, idx)
      };
      return tmpObj;
    });

    /*
      Ensure we have marked a model as selected.
      We may end up with no model marked as selected
      this happens because trims aren't perfect.
      Example of imperfect trim:
      It's possible to have a saved yaris with a trim of 'l' or 'le' from EFC
      (via 2019 Yaris inventory details pages)
      while via getModel/2019/yaris.json, the trims for yaris are:
      'yaris l' and 'yaris le', etc. Therefore they do not match, and we do not have a selected trim
    */
    if (!__getSelectedModel(modelsForCtx)) {
      console.warn('We had a recent vehicle w/ trim, but trims did not match'); // eslint-disable-line no-console
      modelsForCtx[0].selected = true;
    }
    return modelsForCtx;
  }

  // new instance of VehiclePicker created via form.js
  var VehiclePicker = function (el, config) {
    this.$el = $(el);
    this.config = config;
  };

  // load invoked via form.js
  $.extend(VehiclePicker.prototype, {
    load: function () {
      var self = this;
      return PageCache.awaitAll(['series.raw', 'series.map'])
        .then(function (res) {
          self._series = __parseSeries(res[0].seriesDepthOne.series, res[1]);
          return self.buildPickerContext(); // returns a promise that returns a promise. form needs to know when we are truly done
        })
        .catch(function (err) { console.error('err in load: ', err); }); // eslint-disable-line no-console
    },

    buildPickerContext: function () {
      var self = this;
      return self.populateVehicleSelects()
        .then(function (ctx) {
          self.drawPage(ctx);
          self.analyticsAddVehicle();
        })
        .catch(function (err) {
          console.error('VehiclePicker err: ', err); // eslint-disable-line no-console
          console.error('Changing to short form and trying again'); // eslint-disable-line no-console
          self.config.formLength = 'short'; // if fail, switch to short form
          self.drawPage({});
          self.analyticsAddVehicle();
        });
    },

    // Returns a promise. Resolves immediately if short form or if default form and no recent vehicle
    // resolves with an updated HBS Context if long form and vehicle selected
    // This is always working off of the recently saved vehicle
    populateVehicleSelects: function () {
      var self = this;
      var request;
      var vehicleId = recentVehicles.getMostRecentVehicleId();
      self.series = __markSelectedVehicle(self._series, vehicleId);

      if (vehicleId) {
        request = {
          year: vehicleId.split('|')[0],
          series_code: vehicleId.split('|')[1]
        };
      }

      return new Promise(function modelsTrimsProm(resolve, reject) {
        if (!vehicleId || self.config.formLength === 'short') {
          resolve(); // note that majority of TDAs use short forms :)
        } else {
          /*
            We need to get readable "Model" data from "getModel/2019/4runner.json" to populate the "select a model"
            This promise gives us data that looks like:
            ["SR5", "SR5 Premium", "TRD Off-Road", "TRD Off-Road Premium"]
          */
          var cacheIdModels = vehicleId + '.models'; // '2019|4runner.models'
          if (!PageCache.hasOrPending(cacheIdModels)) {
            PageCache.maybeSet(cacheIdModels, batws.cls().getModel(request));
          }

          PageCache.await(cacheIdModels)
            .then(function (res) {
              return __modelsForCtx(res.modelInfo.models);
            })
            .then(function (models) {
              var updatedContext = { models: models };
              var selectedModel = __getSelectedModel(models); // need a selected model to make our next request
              var trimsCacheId = vehicleId + '.' + selectedModel.id + '.trims'; // 2019|avalonhybrid.hybridlimited.trims
              var trimsRequest = $.extend(request, { model: selectedModel.title });

              /*
                We need to get readable "Trim" data from "getTrimInfo/2019/4runner/SR5.json"
                to populate the "select a model".
                This promise gives us data that looks like:
                ["SR5", "SR5 Premium", "TRD Off-Road", "TRD Off-Road Premium"]
              */
              if (!PageCache.hasOrPending(trimsCacheId)) {
                PageCache.maybeSet(trimsCacheId, batws.cls().getTrimInfo(trimsRequest));
              }

              PageCache.await(trimsCacheId)
                .then(function (trimsRes) {
                  var trims = trimsRes.trimInfo.trimDescription;
                  if (!trims.length) {
                    throw new Error('FAILURE');
                  }
                  var mostRecent = recentVehicles.getMostRecentVehicle();
                  function selectedTrimCode(trim, idx) {
                    if (mostRecent && mostRecent.trimCode) {
                      return trim.trimCode === mostRecent.trimCode;
                    }
                    return idx === 0;
                  }
                  var trimsForCtx = trims.map(function (trim, idx) {
                    var tmpObj = {
                      id: trim.trimCode,
                      title: trim.trimName,
                      selected: selectedTrimCode(trim, idx)
                    };
                    return tmpObj;
                  });
                  updatedContext.trims = trimsForCtx;
                  resolve(updatedContext);
                })
                .catch(function (err) {
                  console.error('reject trims. err: ', err); // eslint-disable-line no-console
                  reject(err);
                });
            })
            .catch(function (err) {
              console.error('reject. err: ', err); // eslint-disable-line no-console
              reject(err);
            });
        }
      });
    },

    drawPage: function (ctx) {
      var selectedVehicleId = recentVehicles.getMostRecentVehicleId();
      var self = this;
      var defaultContext = {
        formLength: self.config.formLength,
        formType: self.config.formType,
        image: __vehicleImage(selectedVehicleId),
        i18n: self.config.i18n,
        series: self.series
      };

      var finalCtx = defaultContext;
      if (ctx) {
        finalCtx = $.extend({}, defaultContext, ctx);
      }
      console.log('CTX for VehiclePicker: ', finalCtx); // eslint-disable-line no-console
      // NOT COOL to be redrawing HBS every time?
      self.$el.empty().html(__tmplVehiclePicker(finalCtx));
      self.$selectVehicle = $('select.js-select-vehicle', self.$el);
      self.listeners();
      self.vehicleSlpVals(selectedVehicleId);
      self.saveVehicle();
    },

    // in addition to saving to recent along the way,
    // we need a final save after all the selects have drawn
    // with all the user selected options
    saveVehicle: function () {
      var seriesId = this.$selectVehicle.val();
      recentVehicles.saveVehicle({
        year: seriesId.split('|')[0],
        series: seriesId.split('|')[1],
        trim: $('select.js-select-model', this.$el).val(), // these do NOT line up with trim vals from EFC all of the time
        trimCode: $('select.js-select-trim', this.$el).find(':selected').data('trimcode')
      });
    },

    // 2019|camry
    // Todo: use series_display_name?
    vehicleSlpVals: function (vehicleId) {
      var year = 'N/A';
      var model = 'N/A';
      var trim = 'N/A';
      var $yearEl = $('#vehicleYear', this.$el);
      var $modelEl = $('#vehicleModel', this.$el);
      var $trimEl = $('#vehicleTrim', this.$el);

      if (vehicleId) {
        var id = vehicleId.split('|');
        year = id[0];
        model = id[1];
      }
      // trim we pull directly off of select-trim
      if ($('select.js-select-trim', this.$el).length) {
        trim = $('select.js-select-trim', this.$el).val();
      }

      $yearEl.val(year);
      $modelEl.val(model);
      $trimEl.val(trim);

      $('select.js-select-vehicle, select.js-select-model, select.js-select-trim', this.$el)
        .dropkick({ mobile: true });

      /* console.group('SLP vehicle vals');
      console.log('year: ', $yearEl.val());
      console.log('model: ', $modelEl.val());
      console.log('trim: ', $trimEl.val());
      console.groupEnd(); */
    },

    // TODO: break out shared funcs here
    // note: use select to avoid selecting the dropkicked version with the same class...
    listeners: function listeners() {
      var self = this;
      // todo: move these selectors to self?
      // return year and vehicle from ID in a single func
      self.$selectVehicle.on('change', function selectVehicleChange() {
        var seriesId = $(this).val();

        // By saving the vehicle, we alter the query in buildPickerContext
        recentVehicles.saveVehicle({
          year: seriesId.split('|')[0],
          series: seriesId.split('|')[1]
        });
        self.buildPickerContext();
      });

      if (self.config.formLength === 'default') {
        $('select.js-select-model', this.$el).on('change', function () {
          var seriesId = self.$selectVehicle.val();

          // By saving the vehicle, we alter the query in buildPickerContext
          recentVehicles.saveVehicle({
            year: seriesId.split('|')[0],
            series: seriesId.split('|')[1],
            trim: $(this).val(),
          });
          self.buildPickerContext();
        });

        $('select.js-select-trim', this.$el).on('change', function () {
          var seriesId = self.$selectVehicle.val();
          self.vehicleSlpVals(seriesId);
        });
      }

      if (self.config.formLength === 'short') {
        $('#no-vehicle', this.$el).on('change', function () {
          if (this.checked) {
            self.$selectVehicle.attr('disabled', 'disabled');
            self.$selectVehicle.removeClass('ui-state-error');
            self.$selectVehicle.prev('div.dk-select').removeClass('ui-state-error');
            $('.vehicle-image img', this.$el).attr('src', __vehicleImage());
            self.vehicleSlpVals();
            self.analyticsRemoveVehicle();
          } else {
            self.$selectVehicle.removeAttr('disabled');
            $('.vehicle-image img', this.$el).attr('src', __vehicleImage(self.$selectVehicle.val()));
            self.analyticsAddVehicle();
            self.vehicleSlpVals(self.$selectVehicle.val());
          }
          self.$selectVehicle.dropkick('refresh');
        });
      }
    },

    analyticsAddVehicle: function () {
      var vehicle = recentVehicles.getMostRecentVehicle();
      if (vehicle) {
        anAddContext($('#' + this.config.formType), blocks.seriesBlock({
          series_code: vehicle.series,
          year: vehicle.year,
          grade: vehicle.trim || '',
          trim_code: vehicle.trimCode || ''
        }));
      }
    },

    // outputs an 'empty' dealerBlock. // TODO: provide ability to remove blocks
    analyticsRemoveVehicle: function () {
      anAddContext($('#' + this.config.formType), blocks.seriesBlock({
        series_code: '',
        year: '',
        grade: '',
        trim_code: ''
      }));
    }
  });

  return VehiclePicker;
});

